import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  TextField,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AppleIcon from "@mui/icons-material/Apple";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMutation } from "react-query";
import { GoogleLogin } from "@react-oauth/google";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { sha256 } from "js-sha256";
import client from "../../API";
import routes from "../../Config/routes";
import { validateEmail, validatePassword } from "../../Utils";
import { lightTheme } from "../../Utils/baseTheme";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import useAlertDialog from "../../Components/useAlertDialog";
import AlertDialog from "../../Components/AlertDialog";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import TwoFactorModal from "../../Components/Auth/TwoFactorModal";
import loginImg from "../../Components/globalAssets/login-img.png";
import blitzLogo from "../../Components/globalAssets/blackwhite.png";
import {
  migrateLegacyToken,
  getDeviceTokenByEmail,
  mapEmailToIdentifier,
} from "../../Utils/deviceTokenStorage";

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const RED        = "#EE1D1D";
const RED_LIGHT  = "#FBE1E1";
const GRAY_TEXT  = "#666666";
const BORDER     = "#999999";
const BORDER_HVR = "#444444";
const R_CARD     = "20px";
const R_BTN      = "24px";

/* ─── Theme ─────────────────────────────────────────────────────────────── */
const blitzTheme = createTheme({
  ...lightTheme,
  palette: {
    ...lightTheme.palette,
    primary: { main: RED },
    background: { default: "#F7F7F7", paper: "#FFFFFF" },
  },
  typography: {
    ...lightTheme.typography,
    fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif",
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            fontSize: "15px",
            backgroundColor: "#FFFFFF",
            "& fieldset": { borderColor: BORDER },
            "&:hover fieldset": { borderColor: BORDER_HVR },
            "&.Mui-focused fieldset": { borderColor: RED, borderWidth: "1.5px" },
          },
          "& .MuiInputLabel-root.Mui-focused": { color: RED },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 500, borderRadius: R_BTN },
      },
    },
  },
});

/* ─── Google / PKCE constants ────────────────────────────────────────────── */
const GOOGLE_IOS_CLIENT_ID =
  "489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com";
const GOOGLE_SCOPES = ["openid", "email", "profile"];
const REDIRECT_URI =
  "com.googleusercontent.apps.489265257959-374fsc04dffos29tto4jsqcd2sch5f9o:/oauth2redirect";

const base64url = (uint8) =>
  btoa(String.fromCharCode(...uint8))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

function createPKCE() {
  const rnd = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64url(rnd);
  const hash = sha256.arrayBuffer(verifier);
  const challenge = base64url(new Uint8Array(hash));
  return { verifier, challenge };
}

async function exchangeCodeForIdToken(code, codeVerifier) {
  const body = new URLSearchParams({
    code, client_id: GOOGLE_IOS_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    grant_type: "authorization_code",
    code_verifier: codeVerifier,
  }).toString();
  const res = await fetch("https://oauth2.googleapis.com/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const responseText = await res.text();
  if (!res.ok) {
    let errorMessage = "Google token exchange failed";
    try {
      const e = JSON.parse(responseText);
      errorMessage = e.error_description || e.error || errorMessage;
    } catch (_) {}
    throw new Error(errorMessage);
  }
  const data = JSON.parse(responseText);
  if (!data.id_token) throw new Error("No id_token returned from Google");
  return data.id_token;
}

/* ─── Shared primitives ──────────────────────────────────────────────────── */

/** Section label above a login method (mirrors PersonaOnboardingLayout red label) */
function SectionLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: "12px", fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: RED, mb: 1.5,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {children}
    </Typography>
  );
}

/** Full-width pill action button */
function ActionButton({ children, onClick, disabled, loading, color = "red", sx: sxProp, type = "button" }) {
  const bg = color === "black" ? "#111111" : color === "google" ? "#4285F4" : RED;
  const bgHover = color === "black" ? "#333" : color === "google" ? "#3367D6" : "#c91616";
  return (
    <Box
      component="button"
      type={type}
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "10px", width: "100%", height: { xs: "48px", sm: "52px" },
        px: "24px", border: "none", borderRadius: R_BTN,
        backgroundColor: bg, color: "#FFFFFF",
        fontSize: "15px", fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "opacity 0.15s ease, background-color 0.15s ease",
        fontFamily: "'DM Sans', sans-serif",
        "&:hover:not(:disabled)": { backgroundColor: bgHover },
        ...sxProp,
      }}
    >
      {loading && <CircularProgress size={16} sx={{ color: "#FFFFFF", flexShrink: 0 }} />}
      {children}
    </Box>
  );
}

/** Divider with label */
function OrDivider() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 0.5 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
      <Typography sx={{ fontSize: "12px", color: BORDER, fontFamily: "'DM Sans', sans-serif", userSelect: "none" }}>
        or
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
    </Box>
  );
}

/** Step-indicator accent strip (decorative, mirrors OnboardingStepIndicator) */
function StepAccent() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
      {[true, false, false].map((active, i) => (
        <React.Fragment key={i}>
          <Box sx={{ height: "2px", width: "28px", borderRadius: "1px", backgroundColor: active ? RED : "#E0E0E0" }} />
          {i < 2 && (
            <Box sx={{
              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
              backgroundColor: active ? RED : "transparent",
              border: `1.5px solid ${active ? RED : BORDER}`,
            }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function UnifiedLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* state */
  const [email, setEmail]                   = useState("");
  const [password, setPassword]             = useState("");
  const [showPassword, setShowPassword]     = useState(false);
  const [snack, setSnack]                   = useState({ open: false, msg: "", sev: "info" });
  const [oauthAsk, setOauthAsk]             = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  const [linkingDialog, setLinkingDialog]   = useState({ open: false, googleToken: null, email: "" });
  const [linkPassword, setLinkPassword]     = useState("");
  const [twoFARequired, setTwoFARequired]   = useState(false);
  const [twoFAData, setTwoFAData]           = useState(null);
  const [pendingLoginPayload, setPendingLoginPayload] = useState(null);
  const pkceRef  = useRef(null);
  const tabOpen  = useRef(false);

  const { authorize, setCurrentUser } = useAuth();
  const { login: creatorLogin }       = useCreatorAuth();
  const { dialogState }               = useAlertDialog();

  const redirect    = searchParams.get("redirect");
  const service_id  = searchParams.get("service_id");
  const stateParam  = searchParams.get("state");
  const isWeb       = Capacitor.getPlatform() === "web";

  /* ── device token helper ── */
  const attachDevice = async (payload) => {
    migrateLegacyToken();
    let deviceToken = null;
    let lookupEmail = payload.email;
    if (!lookupEmail && payload.token) {
      try {
        const parts = payload.token.split(".");
        if (parts.length === 3) {
          lookupEmail = JSON.parse(atob(parts[1])).email;
        }
      } catch (_) {}
    }
    if (lookupEmail) deviceToken = getDeviceTokenByEmail(lookupEmail);
    if (deviceToken) return { ...payload, device_token: deviceToken, platform: Capacitor.getPlatform() };
    return payload;
  };

  /* ── mutations ── */
  const handleLoginSuccess = (data) => {
    if (tabOpen.current) Browser.close().catch(() => {});
    setIsGoogleLoading(false);
    setIsAppleLoading(false);
    if (!data.user) {
      setSnack({ open: true, sev: "error", msg: "Login response missing user data. Please try again." });
      return;
    }
    if (!data.token || !data.user.global_user_type) {
      setSnack({ open: true, sev: "error", msg: "Invalid login response. Please try again." });
      return;
    }
    if (data.user.global_user_type === "creator") {
      creatorLogin({ token: data.token, creator_user: data.user });
      navigate(data.redirect === "edit" ? routes.creatorConnectEdit : routes.creatorConnectStart);
    } else {
      authorize({ token: data.token, user: data.user });
      setCurrentUser(data.user);
      goAfterLogin(data.redirect);
    }
  };

  const onError = (err, provider = "Login") => {
    if (err === null) return;
    setSnack({ open: true, sev: "error", msg: "Error occurred on the server side. Please try again." });
    setIsGoogleLoading(false);
    setIsAppleLoading(false);
  };

  const { mutate: unifiedLogin, isLoading: isUnifiedLoading } = useMutation(
    async (payload) => {
      const enhanced = await attachDevice(payload);
      if (!enhanced.token && !enhanced.email) throw new Error("Missing token or email for login");
      return client.users.uniloginV2(enhanced);
    },
    {
      onSuccess: (data, variables) => {
        if (data.status === "2fa_required") {
          setIsGoogleLoading(false); setIsAppleLoading(false);
          setTwoFAData(data); setTwoFARequired(true); setPendingLoginPayload(variables);
          return;
        }
        if (data.status === "link_required") {
          setIsGoogleLoading(false); setIsAppleLoading(false);
          setLinkingDialog({ open: true, googleToken: variables.token, email: data.email });
          return;
        }
        handleLoginSuccess(data);
      },
      onError,
    }
  );

  const { mutate: appleLogin, isLoading: isAppleApiLoading } = useMutation(
    async (payload) => {
      const enhanced = await attachDevice(payload);
      if (!enhanced.identityToken) throw new Error("Missing Apple identity token");
      return client.users.appleLoginv2(enhanced);
    },
    {
      onSuccess: (data, variables) => {
        if (data.status === "2fa_required") {
          setIsAppleLoading(false);
          setTwoFAData(data); setTwoFARequired(true); setPendingLoginPayload(variables);
          return;
        }
        if (data.status === "link_required") {
          setIsAppleLoading(false);
          setSnack({ open: true, sev: "info", msg: "An account with this email already exists. Please use Google login to link accounts." });
          return;
        }
        handleLoginSuccess(data);
      },
      onError: (e) => onError(e, "Apple"),
    }
  );

  const { mutate: linkGoogleAccount, isLoading: isLinkingGoogle } = useMutation(
    async ({ googleToken, password }) => {
      const enhanced = await attachDevice({ google_token: googleToken, password });
      return client.users.linkGoogleLogin(enhanced);
    },
    {
      onSuccess: (data) => {
        if (data.status === "2fa_required") {
          setLinkingDialog({ open: false, googleToken: null, email: "" });
          setLinkPassword("");
          setTwoFAData(data); setTwoFARequired(true);
          return;
        }
        setLinkingDialog({ open: false, googleToken: null, email: "" });
        setLinkPassword("");
        handleLoginSuccess(data);
        setSnack({ open: true, sev: "success", msg: "Google account linked successfully!" });
      },
      onError: () => setSnack({ open: true, sev: "error", msg: "Failed to link Google account. Please try again." }),
    }
  );

  /* ── deep-link handler ── */
  const handleDeepLink = useCallback(async ({ url }) => {
    try {
      const u = new URL(url);
      if (u.protocol.startsWith("com.googleusercontent.apps.") && u.pathname === "/oauth2redirect") {
        if (tabOpen.current) { await Browser.close().catch(() => {}); tabOpen.current = false; }
        const error = u.searchParams.get("error");
        if (error) throw new Error(`Google OAuth error: ${error}`);
        const code = u.searchParams.get("code");
        if (!code) throw new Error("Missing OAuth code");
        setIsGoogleLoading(true);
        const idToken = await exchangeCodeForIdToken(code, pkceRef.current?.verifier);
        if (!idToken) throw new Error("Failed to obtain Google id_token");
        unifiedLogin({ token: idToken });
      }
    } catch (err) {
      setSnack({ open: true, sev: "error", msg: "Google login failed. Please try again." });
      setIsGoogleLoading(false);
    }
  }, [unifiedLogin]);

  /* ── effects ── */
  useEffect(() => { if (service_id) setOauthAsk(true); }, [service_id]);

  useEffect(() => {
    if (!isWeb) {
      const appListener = App.addListener("appUrlOpen", handleDeepLink);
      const browserListener = Browser.addListener("browserFinished", () => {
        tabOpen.current = false;
        setIsGoogleLoading(false);
      });
      return () => { appListener.remove(); browserListener.remove(); };
    }
  }, [isWeb, handleDeepLink]);

  useEffect(() => { setIsLoadingTokens(false); }, []);

  /* ── navigation ── */
  const goAfterLogin = (loginRedirect) => {
    if (redirect?.startsWith("/")) return navigate(redirect);
    if (loginRedirect === "edit") return navigate(routes.creatorConnectEdit);
    if (redirect === "oauth") {
      const q = new URLSearchParams({ service_id, state: stateParam ?? "" });
      return navigate(`${routes.creatorConnectOauth}?${q}`);
    }
    navigate(routes.dashboard);
  };

  /* ── form handlers ── */
  const emailLogin = (e) => {
    e.preventDefault();
    if (!validateEmail(email)) { setSnack({ open: true, sev: "error", msg: "Invalid email" }); return; }
    if (!validatePassword(password)) { setSnack({ open: true, sev: "error", msg: "Weak password" }); return; }
    unifiedLogin({ email, password });
  };

  const handle2FASuccess = async (twoFASuccessData) => {
    const emailForLookup = pendingLoginPayload?.email || twoFASuccessData?.email;
    let deviceToken = null;
    if (emailForLookup) deviceToken = getDeviceTokenByEmail(emailForLookup);

    if (twoFASuccessData && pendingLoginPayload) {
      const emailVal = pendingLoginPayload.email || twoFASuccessData.email;
      const identifiers = { user_id: twoFASuccessData.user_id, creator_username: twoFASuccessData.creator_username, client_id: twoFASuccessData.client_id };
      if (emailVal && (identifiers.user_id || identifiers.creator_username || identifiers.client_id)) {
        mapEmailToIdentifier(emailVal, identifiers);
      }
    }

    if (pendingLoginPayload) {
      const enhanced = { ...pendingLoginPayload };
      if (deviceToken) enhanced.device_token = deviceToken;
      if (enhanced.identityToken) appleLogin(enhanced);
      else if (enhanced.token || enhanced.email) unifiedLogin(enhanced);
      setPendingLoginPayload(null);
    } else {
      window.location.reload();
    }
  };

  const webGoogleSuccess = (res) => { setIsGoogleLoading(true); unifiedLogin({ token: res.credential }); };
  const webGoogleFail    = () => setSnack({ open: true, sev: "error", msg: "Google login failed. Please try again." });

  const nativeGoogle = async () => {
    try {
      pkceRef.current = createPKCE();
      tabOpen.current = true;
      setIsGoogleLoading(true);
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
        client_id: GOOGLE_IOS_CLIENT_ID, redirect_uri: REDIRECT_URI,
        response_type: "code", code_challenge: pkceRef.current.challenge,
        code_challenge_method: "S256", scope: GOOGLE_SCOPES.join(" "), prompt: "select_account",
      }).toString()}`;
      await Browser.open({ url: authUrl });
    } catch (err) {
      setSnack({ open: true, sev: "error", msg: "An error occurred with Google login. Please try again." });
      setIsGoogleLoading(false);
      tabOpen.current = false;
    }
  };

  const handleApple = async () => {
    if (isWeb) { setSnack({ open: true, sev: "info", msg: "Apple login is available only in the iOS app." }); return; }
    try {
      setIsAppleLoading(true);
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const credential = await SignInWithApple.authorize({ scopes: ["email", "name"] });
      const identityToken = credential.identityToken ?? credential?.response?.identityToken;
      if (!identityToken) throw new Error("Apple identity token missing");
      appleLogin({ identityToken });
    } catch (err) {
      setSnack({ open: true, sev: "error", msg: "Issue connecting with Apple. Please try again." });
      setIsAppleLoading(false);
    }
  };

  const anyLoading = isUnifiedLoading || isGoogleLoading || isAppleLoading || isAppleApiLoading;

  if (isLoadingTokens) return null;

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <ThemeProvider theme={blitzTheme}>
      <CssBaseline />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
      <AlertDialog alertState={dialogState} />

      {/* ── OAuth prompt dialog ── */}
      <Dialog
        open={oauthAsk}
        onClose={() => setOauthAsk(false)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: "420px", width: "100%" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em", fontSize: "20px" }}>
          Connecting to an App with Blitz
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: GRAY_TEXT, fontSize: "15px" }}>
            To use this app, you'll need a CreatorConnect account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="contained"
            component={Link}
            to={`${routes.creatorConnectOnboardApp}?service_id=${service_id ?? ""}&state=${stateParam ?? ""}`}
            sx={{ borderRadius: R_BTN, backgroundColor: RED, textTransform: "none", fontWeight: 600 }}
          >
            I'm new to CreatorConnect
          </Button>
          <Button
            onClick={() => setOauthAsk(false)}
            sx={{ borderRadius: R_BTN, color: GRAY_TEXT, textTransform: "none", fontWeight: 500 }}
          >
            I have an account
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Account linking dialog ── */}
      <Dialog
        open={linkingDialog.open}
        onClose={() => { setLinkingDialog({ open: false, googleToken: null, email: "" }); setLinkPassword(""); }}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: "420px", width: "100%" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em", fontSize: "20px" }}>
          Link Your Google Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: GRAY_TEXT, fontSize: "15px", mb: 2 }}>
            An account with the email <strong>{linkingDialog.email}</strong> already exists. Enter your Blitz password to link your Google account.
          </DialogContentText>
          <TextField
            autoFocus fullWidth margin="dense" label="Password" type="password"
            value={linkPassword}
            onChange={(e) => setLinkPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && linkPassword) {
                linkGoogleAccount({ googleToken: linkingDialog.googleToken, password: linkPassword });
              }
            }}
            disabled={isLinkingGoogle}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => { setLinkingDialog({ open: false, googleToken: null, email: "" }); setLinkPassword(""); }}
            disabled={isLinkingGoogle}
            sx={{ borderRadius: R_BTN, color: GRAY_TEXT, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => linkGoogleAccount({ googleToken: linkingDialog.googleToken, password: linkPassword })}
            disabled={!linkPassword || isLinkingGoogle}
            sx={{ borderRadius: R_BTN, backgroundColor: RED, textTransform: "none", fontWeight: 600 }}
          >
            {isLinkingGoogle ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Link Account"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Page shell ── */}
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#F7F7F7", fontFamily: "'DM Sans', sans-serif" }}>
        <HomeHeader />

        <Box
          component="main"
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "stretch",
            mt: "64px", // clear fixed header
          }}
        >
          {/* ── Left panel: image ── */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              width: "48%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={loginImg}
              alt="Blitz Login"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {/* Back to home overlay */}
            <Box
              component="button"
              type="button"
              onClick={() => { window.location.href = "/"; }}
              sx={{
                position: "absolute", top: "20px", left: "20px",
                display: "flex", alignItems: "center", gap: "6px",
                height: "38px", px: "16px", border: "none", borderRadius: "20px",
                backgroundColor: "rgba(255,255,255,0.22)",
                backdropFilter: "blur(8px)",
                color: "#FFFFFF", fontSize: "13px", fontWeight: 600,
                cursor: "pointer", transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.38)" },
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              ← Back to Home
            </Box>
            {/* Logo watermark */}
            <Box
              component="img"
              src={blitzLogo}
              alt="Blitz"
              sx={{ position: "absolute", bottom: "20px", right: "20px", width: "52px", height: "auto", opacity: 0.9 }}
            />
          </Box>

          {/* ── Right panel: form ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 3, sm: 5, md: 6 },
              py: { xs: 4, sm: 6 },
            }}
          >
            {/* Mobile: nav row */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                width: "100%", maxWidth: "440px",
                justifyContent: "space-between", alignItems: "center",
                mb: 3,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => navigate(routes.base)}
                sx={{
                  border: "none", background: "none",
                  fontSize: "14px", fontWeight: 500, color: GRAY_TEXT,
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >
                ← Back
              </Box>
              <Box component="img" src={blitzLogo} alt="Blitz" sx={{ width: "36px", height: "auto" }} />
            </Box>

            {/* Form container */}
            <Box sx={{ width: "100%", maxWidth: "440px" }}>

              {/* Heading */}
              <Box sx={{ mb: 4 }}>
                <Typography sx={{
                  fontSize: "12px", fontWeight: 700, letterSpacing: "0.07em",
                  textTransform: "uppercase", color: RED, mb: 1,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Blitz
                </Typography>
                <Typography sx={{
                  fontSize: { xs: "30px", sm: "36px" }, fontWeight: 700,
                  letterSpacing: "-0.03em", lineHeight: 1.12, color: "#111111",
                  fontFamily: "'DM Sans', sans-serif", mb: 1,
                }}>
                  Sign in
                </Typography>
                <Typography sx={{
                  fontSize: "16px", fontWeight: 400, color: GRAY_TEXT,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Choose how you'd like to continue
                </Typography>
              </Box>

              {/* ── Social login card ── */}
              <Box
                sx={{
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: R_CARD,
                  p: { xs: "20px", sm: "24px" },
                  backgroundColor: "#FFFFFF",
                  mb: 2,
                }}
              >
                <SectionLabel>Creators &amp; Advanced Users</SectionLabel>

                {/* Google */}
                {isWeb ? (
                  <Box sx={{
                    "& > div": { width: "100% !important" },
                    "& iframe": { borderRadius: "14px !important" },
                    mb: 2,
                  }}>
                    <GoogleLogin
                      onSuccess={webGoogleSuccess}
                      onError={webGoogleFail}
                      disabled={anyLoading}
                      theme="filled_blue"
                      size="large"
                      ux_mode="popup"
                      width="100%"
                      shape="rectangular"
                    />
                  </Box>
                ) : (
                  <ActionButton
                    color="google"
                    onClick={nativeGoogle}
                    disabled={anyLoading}
                    loading={isGoogleLoading}
                    sx={{ mb: 2 }}
                  >
                    Sign in with Google
                  </ActionButton>
                )}

                {/* Apple */}
                <ActionButton
                  color="black"
                  onClick={handleApple}
                  disabled={anyLoading}
                  loading={isAppleLoading || isAppleApiLoading}
                >
                  <AppleIcon sx={{ fontSize: "20px" }} />
                  Sign in with Apple
                </ActionButton>
              </Box>

              {/* ── Email / password card ── */}
              <Box
                component="form"
                onSubmit={emailLogin}
                noValidate
                sx={{
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: R_CARD,
                  p: { xs: "20px", sm: "24px" },
                  backgroundColor: "#FFFFFF",
                  mb: 3,
                }}
              >
                <SectionLabel>Brands &amp; Agencies</SectionLabel>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth required
                    autoComplete="email"
                    label="Email address"
                    type="email"
                    value={email}
                    disabled={anyLoading}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    fullWidth required
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    label="Password"
                    value={password}
                    disabled={anyLoading}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Forgot + submit row */}
                  <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    mt: "-4px",
                  }}>
                    <Link
                      to={routes.passwordForgot}
                      style={{
                        fontSize: "14px", color: RED, fontWeight: 500,
                        textDecoration: "none", fontFamily: "'DM Sans', sans-serif",
                        alignSelf: "center",
                      }}
                    >
                      Forgot password?
                    </Link>

                    <Box
                      component="button"
                      type="submit"
                      disabled={anyLoading}
                      sx={{
                        height: "44px", px: "32px",
                        border: "none", borderRadius: R_BTN,
                        backgroundColor: RED, color: "#FFFFFF",
                        fontSize: "15px", fontWeight: 600,
                        cursor: anyLoading ? "not-allowed" : "pointer",
                        opacity: anyLoading ? 0.55 : 1,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        transition: "opacity 0.15s ease",
                        fontFamily: "'DM Sans', sans-serif",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      {isUnifiedLoading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
                      Sign in
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* ── Footer row ── */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}>
                <Typography sx={{ fontSize: "14px", color: GRAY_TEXT, fontFamily: "'DM Sans', sans-serif" }}>
                  No account?{" "}
                  <Box
                    component="span"
                    onClick={() => navigate(routes.requestAccess)}
                    sx={{
                      color: RED, fontWeight: 600, cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Contact us
                  </Box>
                </Typography>
                <StepAccent />
              </Box>
            </Box>
          </Box>
        </Box>

        <HomeFooter />
      </Box>

      {/* ── 2FA modal ── */}
      <TwoFactorModal
        isOpen={twoFARequired}
        onClose={() => { setTwoFARequired(false); setTwoFAData(null); setPendingLoginPayload(null); }}
        email={pendingLoginPayload?.email || twoFAData?.email}
        userId={twoFAData?.user_id}
        creatorUsername={twoFAData?.creator_username}
        clientId={twoFAData?.client_id}
        twofaToken={twoFAData?.twofa_token}
        phoneAvailable={twoFAData?.phone_available}
        emailAvailable={twoFAData?.email_available}
        preferredMethod={twoFAData?.preferred_method}
        onSuccess={handle2FASuccess}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.sev} sx={{ width: "100%", borderRadius: "14px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}